import React, { useState, useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { 
  Button, TextField, Paper, Typography, CircularProgress, Box, 
  useTheme, Container, Grid, Link, FormControlLabel, Checkbox,
  InputAdornment, IconButton, useMediaQuery, Grow
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import KitchenImg from '../Assets/Kitchen/kitchenimg.png';
import Preloader from '../Components/Preloader/Preloader';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [email, setEmail] = useState('mohd.yahiya7@gmail.com');
  const [password, setPassword] = useState('Yahiya@123');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [login, { loading, error }] = useMutation(LOGIN_MUTATION);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [animate, setAnimate] = useState(false);
  
  useEffect(() => {
    setAnimate(true);
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    } else if (!re.test(email)) {
      setEmailError('Please enter a valid email');
      return false;
    } else {
      setEmailError('');
      return true;
    }
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  const handleLogin = async () => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (isEmailValid && isPasswordValid) {
      try {
        const { data } = await login({ variables: { email, password } });
        if (data && data.login) {
          localStorage.setItem('autoChef_authToken', data.login);
          setAuth(true);
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Login error:', err);
      }
    }
  };

  return (
    <>
      {loading ? <Preloader /> : (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            backgroundImage: isSmallScreen ? `url(${KitchenImg})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          <Container maxWidth="lg" sx={{ m: 'auto', py: 2 }}>
            <Grow in={animate} timeout={800}>
              <Grid container 
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: theme.shadows[10],
                }}
              >
                {/* Kitchen Image Section - Hidden on small screens */}
                {!isSmallScreen && (
                  <Grid item xs={12} md={7} sx={{ position: 'relative', overflow: 'hidden', p: 0 }}>
                    <motion.img
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.5, duration: 1 }}
                      src={KitchenImg}
                      alt="Kitchen Image"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        position: 'absolute',
                      }}
                    />
                    <Box 
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        borderRadius: 2,
                        p: 4,
                        m: 4,
                        color: 'white'
                      }}
                    >
                      <Typography variant="h3" fontWeight="bold" mb={2}>
                        Welcome to AutoChef
                      </Typography>
                      <Typography variant="h6">
                        Sign in to manage your account and access all features
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {/* Login Form */}
                <Grid item xs={12} md={5}
                  sx={{
                    backgroundColor: theme.palette.secondary.paper ,
                    backdropFilter: isSmallScreen ? 'blur(10px)' : 'none',
                    p: { xs: 3, sm: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 3
                    }}
                  >
                    <Box sx={{
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      p: 1,
                      mb: 2
                    }}>
                      <LockOutlinedIcon fontSize="large" sx={{ color: 'white' }} />
                    </Box>
                    <Typography variant="h4" fontWeight="bold">
                      Sign In
                    </Typography>
                    {isSmallScreen && (
                      <Typography variant="body1" textAlign="center" mt={1}>
                        Welcome to AutoChef! Please sign in to continue
                      </Typography>
                    )}
                  </Box>
                  
                  <TextField
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    onBlur={() => validateEmail(email)}
                  />
                  
                  <TextField
                    label="Password"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                    onBlur={() => validatePassword(password)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%',
                    my: 1
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={rememberMe} 
                          onChange={e => setRememberMe(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Remember me"
                    />
                    <Link component={RouterLink} to="#" variant="body2">
                      Forgot password?
                    </Link>
                  </Box>
                  
                  {error && (
                    <Typography color="error" sx={{ mt: 1, textAlign: 'center' }}>
                      Invalid credentials. Please try again.
                    </Typography>
                  )}
                  
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    sx={{ mt: 2, py: 1.5 }}
                    onClick={handleLogin}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign In'}
                  </Button>
                  
                  {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2">
                      Don't have an account?{' '}
                      <Link component={RouterLink} to="/register" fontWeight="bold">
                        Sign up now
                      </Link>
                    </Typography>
                  </Box> */}
                </Grid>
              </Grid>
            </Grow>
          </Container>
        </Box>
      )}
    </>
  );
};

export default LoginPage;
